import React, { useState, useEffect } from 'react';
import {
  WorkflowFunction,
  SaveDraftRequest,
  SaveFinalRequest,
  workflowApi,
} from '../services/api';
import './SaveWorkflowModal.css';

interface SaveWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  saveType: 'draft' | 'final';
  onSaveComplete: (workflowId: string, requiresUAT?: boolean) => void;
  currentScript?: string;
  currentBreakdown?: any;
  // New props for complete state
  workflowContextId?: string;
  currentPhase?: number;
  fileMetadata?: Array<{ file_id: string; file_name: string; file_type: string; size: number }>;
  columnMappings?: Record<string, any>;
  requirementsAnalysis?: string;
  qaResults?: any;
}

export const SaveWorkflowModal: React.FC<SaveWorkflowModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  saveType,
  onSaveComplete,
  currentScript,
  currentBreakdown,
  workflowContextId,
  currentPhase = 1,
  fileMetadata,
  columnMappings,
  requirementsAnalysis,
  qaResults,
}) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [functionCategoryId, setFunctionCategoryId] = useState<string>('');
  const [functions, setFunctions] = useState<WorkflowFunction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instructionSummary, setInstructionSummary] = useState<string>('');
  const [workflowId, setWorkflowId] = useState<string>('');
  const [originalPrompt, setOriginalPrompt] = useState<string>('');
  const [suggestedName, setSuggestedName] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setStep(1);
      setName('');
      setDescription('');
      setFunctionCategoryId('');
      setError(null);
      setInstructionSummary('');
      setOriginalPrompt('');
      setSuggestedName('');
      
      // Load function categories
      loadFunctions();
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const loadFunctions = async () => {
    try {
      const response = await workflowApi.listFunctions();
      setFunctions(response.data);
    } catch (err) {
      console.error('Failed to load function categories:', err);
      setError('Failed to load function categories');
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data: SaveDraftRequest = {
        session_id: sessionId,
        name,
        description,
        function_category_id: functionCategoryId || undefined,
        ui_state: {
          activeTab: 'files',
          sidebarOpen: true,
          // Will be enhanced with more UI state from WorkflowSidebar
        },
        script_breakdown: currentBreakdown || undefined,
        // Include complete state for self-contained drafts
        workflow_context_id: workflowContextId,
        current_phase: currentPhase,
        column_mappings: columnMappings,
        requirements_analysis: requirementsAnalysis,
        qa_results: qaResults,
      };
      
      const response = await workflowApi.saveDraft(data);
      onSaveComplete(response.data.id, false);  // false = no UAT required for drafts
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFinal = async () => {
    if (step === 1) {
      // Move to step 2: Review summary
      setLoading(true);
      setError(null);
      
      try {
        const data: SaveFinalRequest = {
          session_id: sessionId,
          name,
          description,
          function_category_id: functionCategoryId,
          script_breakdown: currentBreakdown || undefined,
          // Include complete state for self-contained final workflows
          workflow_context_id: workflowContextId,
          script_code: currentScript,
          file_metadata: fileMetadata,
          requirements_analysis: requirementsAnalysis,
          column_mappings: columnMappings,
          qa_analysis: qaResults?.analysis || qaResults?.qa_analysis,
        };
        
        const response = await workflowApi.saveFinal(data);
        setInstructionSummary(response.data.instruction_summary);
        setWorkflowId(response.data.workflow_id);
        setOriginalPrompt(response.data.original_prompt || '');
        setSuggestedName(response.data.suggested_name || name);
        
        // If no name was provided, use the suggested name
        if (!name || name.trim() === '') {
          setName(response.data.suggested_name);
        }
        
        setStep(2);
        
        // Store workflow ID and name for UAT step
        sessionStorage.setItem('pending_workflow_id', response.data.workflow_id);
        sessionStorage.setItem('pending_workflow_name', name || response.data.suggested_name);
      } catch (err: any) {
        console.error('Failed to save workflow:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to prepare workflow';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    } else if (step === 2) {
      // Complete the save and trigger UAT modal directly
      onSaveComplete(workflowId, true);
      onClose();
    }
  };

  const handleNext = () => {
    if (saveType === 'draft') {
      handleSaveDraft();
    } else {
      handleSaveFinal();
    }
  };

  const canProceed = () => {
    if (saveType === 'draft') {
      return name.trim().length > 0;
    } else {
      return name.trim().length > 0 && functionCategoryId;
    }
  };

  const renderFunctionOptions = (funcs: WorkflowFunction[], level = 0): JSX.Element[] => {
    const options: JSX.Element[] = [];
    
    for (const func of funcs) {
      options.push(
        <option key={func.id} value={func.id}>
          {'  '.repeat(level)}{func.name} ({func.code})
        </option>
      );
      
      if (func.children && func.children.length > 0) {
        options.push(...renderFunctionOptions(func.children, level + 1));
      }
    }
    
    return options;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container save-workflow-modal">
        <div className="modal-header">
          <h2>
            {saveType === 'draft' ? 'Save Workflow as Draft' : 'Save Workflow as Final'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {step === 1 && (
            <>
              <div className="form-group">
                <label htmlFor="workflow-name">
                  Workflow Name <span className="required">*</span>
                </label>
                <input
                  id="workflow-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Monthly Bank Reconciliation"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="workflow-description">
                  Description
                </label>
                <textarea
                  id="workflow-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of what this workflow does..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="function-category">
                  Function Category {saveType === 'final' && <span className="required">*</span>}
                </label>
                <select
                  id="function-category"
                  value={functionCategoryId}
                  onChange={(e) => setFunctionCategoryId(e.target.value)}
                  required={saveType === 'final'}
                >
                  <option value="">Select a category...</option>
                  {renderFunctionOptions(functions)}
                </select>
              </div>

              {saveType === 'draft' && (
                <div className="info-message">
                  <p>
                    Saving as draft will allow you to continue working on this workflow later.
                    All your current progress will be saved.
                  </p>
                </div>
              )}

              {saveType === 'final' && (
                <div className="info-message">
                  <p>
                    Saving as final will create a reusable workflow template. 
                    You'll need to complete UAT before the workflow can be used by others.
                  </p>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <h3>Review Workflow Summary</h3>
              
              {originalPrompt && (
                <div className="summary-section">
                  <h4>Original Request</h4>
                  <div className="original-prompt">
                    <p>{originalPrompt}</p>
                  </div>
                </div>
              )}
              
              <div className="summary-section">
                <h4>AI-Generated Summary</h4>
                <div className="instruction-summary">
                  <p>{instructionSummary}</p>
                </div>
                <textarea
                  value={instructionSummary}
                  onChange={(e) => setInstructionSummary(e.target.value)}
                  placeholder="Edit the summary if needed..."
                  rows={6}
                  className="summary-editor"
                />
                <p className="help-text">
                  This summary describes what the workflow does functionally. 
                  You can edit it to better reflect your organization's terminology.
                </p>
              </div>
              
              {/* Preview of data being saved */}
              <div className="summary-section">
                <h4>Data Being Saved</h4>
                <div className="data-preview">
                  <div className="preview-item">
                    <span className="preview-label">Workflow Name:</span>
                    <span className="preview-value">{name}</span>
                  </div>
                  {description && (
                    <div className="preview-item">
                      <span className="preview-label">Description:</span>
                      <span className="preview-value">{description}</span>
                    </div>
                  )}
                  {fileMetadata && fileMetadata.length > 0 && (
                    <div className="preview-item">
                      <span className="preview-label">Files:</span>
                      <span className="preview-value">{fileMetadata.length} file(s) included</span>
                    </div>
                  )}
                  {currentScript && (
                    <div className="preview-item">
                      <span className="preview-label">Script:</span>
                      <span className="preview-value">✓ Generated script included</span>
                    </div>
                  )}
                  {requirementsAnalysis && (
                    <div className="preview-item">
                      <span className="preview-label">Requirements:</span>
                      <span className="preview-value">✓ Requirements analysis included</span>
                    </div>
                  )}
                  {columnMappings && Object.keys(columnMappings).length > 0 && (
                    <div className="preview-item">
                      <span className="preview-label">Column Mappings:</span>
                      <span className="preview-value">{Object.keys(columnMappings).length} mappings</span>
                    </div>
                  )}
                  {qaResults && (
                    <div className="preview-item">
                      <span className="preview-label">QA Results:</span>
                      <span className="preview-value">✓ QA analysis included</span>
                    </div>
                  )}
                  <div className="preview-item">
                    <span className="preview-label">Current Phase:</span>
                    <span className="preview-value">Phase {currentPhase}</span>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          {step > 1 && (
            <button 
              className="btn btn-secondary" 
              onClick={() => setStep(step - 1)}
              disabled={loading}
            >
              Back
            </button>
          )}
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
            disabled={!canProceed() || loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                <span>Saving...</span>
              </>
            ) : (
              saveType === 'draft' ? 'Save' : 
              step === 2 ? 'Continue to UAT' : 'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};